import { Component, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { LucideAngularModule } from 'lucide-angular';
import { JOURNEY_MAX_LEVEL } from 'src/app/constants/journey-gamification';
import { JourneyService } from '../../services/journey.service';
import { JourneyPhase, JourneyProgress } from '../../models/journey.models';
import { JourneyProgressRingComponent } from '../journey-progress-ring/journey-progress-ring.component';
import { JourneyPathComponent } from '../journey-path/journey-path.component';

/**
 * Progression header of the onboarding journey: one ring for the whole journey, the level
 * earned so far, the two track counters and the milestone path.
 *
 * Shared by the Getting Started page and the onboarding popup so both speak the same visual
 * language; `compact` is the popup variant. It reads `JourneyService` directly rather than
 * taking a dozen inputs — the service is root-provided and already the single source of the
 * progression.
 */
@Component({
  selector: 'app-journey-header',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    LucideAngularModule,
    JourneyProgressRingComponent,
    JourneyPathComponent,
  ],
  templateUrl: './journey-header.component.html',
})
export class JourneyHeaderComponent {
  private journeyService = inject(JourneyService);

  /** Popup variant: smaller ring, no path labels, read-only milestones. */
  compact = input(false);

  phaseSelected = output<JourneyPhase>();

  overallPercent = this.journeyService.overallPercent;
  level = this.journeyService.level;
  levelTitleKey = this.journeyService.levelTitleKey;
  phases = this.journeyService.phases;
  completedCount = this.journeyService.completedCount;
  totalCount = this.journeyService.totalCount;

  readonly maxLevel = JOURNEY_MAX_LEVEL;

  levelParams = computed(() => ({ level: this.level(), max: this.maxLevel }));

  summaryParams = computed(() => ({
    completed: this.completedCount(),
    total: this.totalCount(),
    badges: this.level(),
    maxBadges: this.maxLevel,
  }));

  /** The two tracks stay visible: they are counted separately on purpose. */
  tracks = computed(() => [
    {
      titleKey: 'help.onboarding.journey.tracks.userTitle',
      hintKey: 'help.onboarding.journey.tracks.userHint',
      icon: 'user',
      progress: this.journeyService.userProgress(),
    },
    {
      titleKey: 'help.onboarding.journey.tracks.companyTitle',
      hintKey: 'help.onboarding.journey.tracks.companyHint',
      icon: 'building-2',
      progress: this.journeyService.companyProgress(),
    },
  ]);

  percent(progress: JourneyProgress): number {
    return progress.total === 0
      ? 0
      : Math.round((progress.completed / progress.total) * 100);
  }
}
